import React, { Component } from "react";
import Helmet from "react-helmet";
import { connect } from "react-redux";

import moment from "moment";

import { Icon, InputNumber, Modal, Tooltip, notification } from "antd";

import { title } from "utils";
import { Horloge, Logo, CarouselPhotos } from "components";
import { Chart } from "components/Dashboard";
import { AbilityContext } from "constants/abilityContext";
import { sections } from "constants/dashboard";
import { submissionService, noteService, notificationService } from "services";
import SubmissionPage from "./Submission/Submission";
import {
  NOTE_TYPE_SSE,
  SUBMISSION_STATE_DISCUSSION,
  SUBMISSION_STATE_NOTWON,
  SUBMISSION_STATE_SUBMITTING,
  SUBMISSION_STATE_WAITING,
  SUBMISSION_STATE_WON,
  interventionType,
} from "constants/dbValues";

const REFRESH_MS = 300000; // 5 min
const PUSH_DEBOUNCE_MS = 2000;

const SSE_VS_TAILGATES = sections
  .reduce((all, s) => all.concat(s.charts), [])
  .find((c) => c.title === "Interventions SSE vs. Tailgates");

const calendarDay = (value) => moment(moment.utc(value).format("YYYY-MM-DD"));

// Whole days elapsed since a date (negative for dates still ahead).
const daysSince = (value) => moment().startOf("day").diff(calendarDay(value), "days");

const SUBMITTING_RED_DAYS = 2;
const SUBMITTING_YELLOW_DAYS = 7;

const submittingBadge = (sub) => {
  if (!sub.date_depot) return { key: "none", label: "—" };

  const days = -daysSince(sub.date_depot);
  const key =
    days <= SUBMITTING_RED_DAYS
      ? "overdue"
      : days <= SUBMITTING_YELLOW_DAYS
      ? "soon"
      : "later";

  if (days < 0) return { key, label: "Retard" };
  return { key, label: days === 0 ? "Auj." : `${days} JR` };
};

const WAITING_YELLOW_DAYS = 2;
const WAITING_RED_DAYS = 7;

const waitingBadge = (sub) => {
  if (!sub.date_depot) return { key: "none", label: "—" };

  const days = daysSince(sub.date_depot);
  if (days < 0) return { key: "none", label: "—" };

  const key =
    days > WAITING_RED_DAYS ? "overdue" : days > WAITING_YELLOW_DAYS ? "soon" : "later";
  return { key, label: days === 0 ? "Auj." : `${days} JR` };
};

const daysSinceStamp = (value) =>
  moment().startOf("day").diff(moment(value).startOf("day"), "days");

const followUpBadge = (sub) => {
  if (!sub.date_last_follow_up) return null;

  const days = Math.max(0, daysSinceStamp(sub.date_last_follow_up));
  const key =
    days > WAITING_RED_DAYS ? "overdue" : days > WAITING_YELLOW_DAYS ? "soon" : "later";
  return { key, label: days === 0 ? "Auj." : `${days} JR` };
};

const discussionBadge = (sub) => {
  if (!sub.date_status_change) return { key: "none", label: "—" };

  const days = Math.max(0, daysSinceStamp(sub.date_status_change));
  return { key: "none", label: days === 0 ? "Auj." : `${days} JR` };
};

const followUpCardKey = (sub) => ((sub.follow_up_count || 0) > 0 ? "none" : "later");

const byDepotDate = (a, b) => {
  if (!a.date_depot) return 1;
  if (!b.date_depot) return -1;
  return moment(a.date_depot).diff(moment(b.date_depot));
};

// Obtenu / Non obtenu roll off the board once the decision is this old. The API
// applies the window — see fetchSubmissions.
const DECIDED_MAX_AGE_DAYS = 5;

const byStatusChange = (a, b) => moment(a.date_status_change).diff(moment(b.date_status_change));

// Most recently decided first.
const byStatusChangeDesc = (a, b) => byStatusChange(b, a);

const BUCKET_BY_STATE = {
  [SUBMISSION_STATE_SUBMITTING]: "onGoing",
  [SUBMISSION_STATE_WAITING]: "waiting",
  [SUBMISSION_STATE_DISCUSSION]: "discussion",
  [SUBMISSION_STATE_WON]: "won",
  [SUBMISSION_STATE_NOTWON]: "notWon",
};

const DROP_TARGETS_FROM = {
  [SUBMISSION_STATE_SUBMITTING]: [SUBMISSION_STATE_WAITING],
};

const STATE_LABELS = {
  [SUBMISSION_STATE_SUBMITTING]: "En soumission",
  [SUBMISSION_STATE_WAITING]: "En attente",
  [SUBMISSION_STATE_DISCUSSION]: "En discussion",
  [SUBMISSION_STATE_WON]: "Obtenu",
  [SUBMISSION_STATE_NOTWON]: "Non obtenu",
};

const AMOUNT_REQUIRED_STATES = [
  SUBMISSION_STATE_WAITING,
  SUBMISSION_STATE_DISCUSSION,
  SUBMISSION_STATE_WON,
  SUBMISSION_STATE_NOTWON,
];

const canMove = (fromState, toState) => {
  if (fromState === toState) return false;
  const allowed = DROP_TARGETS_FROM[fromState];
  return !allowed || allowed.includes(toState);
};

const SORT_BY_BUCKET = {
  onGoing: byDepotDate,
  waiting: byDepotDate,
  discussion: byStatusChange,
  won: byStatusChangeDesc,
  notWon: byStatusChangeDesc,
};

const initials = (name) => {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
};

class HomeTvSubmissionsPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      onGoing: [],
      waiting: [],
      discussion: [],
      notWon: [],
      won: [],
      interventions: [],
      refreshes: 0,
      loading: true,
      followingUp: {},
      dragging: null,
      dragOverState: null,
      editingSubmissionId: null,
      amountPrompt: null,
    };

    // Prevent refresh while dragging submission
    this.pendingMoves = 0;
    this.refreshMissed = false;
    this.fetchAll = this.fetchAll.bind(this);
    this.fetchSubmissions = this.fetchSubmissions.bind(this);
    this.fetchInterventions = this.fetchInterventions.bind(this);
    this.followUp = this.followUp.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchAll();
    this.refreshTimer = setInterval(this.fetchAll, REFRESH_MS);
    this.subscribeToChanges();
  }

  componentWillUnmount() {
    this._isMounted = false;
    clearInterval(this.refreshTimer);
    clearTimeout(this.pushTimer);
    if (this.cableSub) this.cableSub.unsubscribe();
  }

  subscribeToChanges() {
    try {
      notificationService.ensureConsumer(this.props.userId);
      this.cableSub = notificationService.subscribeAll((data) => {
        if (!data || data.type !== "Submission") return;
        clearTimeout(this.pushTimer);
        this.pushTimer = setTimeout(this.fetchSubmissions, PUSH_DEBOUNCE_MS);
      });
    } catch {
      // Pas de temps réel : on s'en remet à l'intervalle.
    }
  }

  // Replay refresh once drag ended
  catchUpRefresh() {
    if (this.pendingMoves > 0 || this.dragged || !this.refreshMissed) return;
    this.refreshMissed = false;
    this.fetchSubmissions();
  }

  fetchAll() {
    this.fetchSubmissions();
    this.fetchInterventions();
    // Bump the counter so the reused dashboard Chart re-fetches its data too.
    if (this._isMounted) this.setState((prev) => ({ refreshes: prev.refreshes + 1 }));
  }

  async fetchSubmissions() {
    if (this.pendingMoves > 0 || this.dragged) {
      this.refreshMissed = true;
      return;
    }

    const group = (rows, id) => rows.filter((s) => s.submission_state.id === id);

    try {
      const inProgress = await submissionService.list({
        submission_state_id: [
          SUBMISSION_STATE_SUBMITTING,
          SUBMISSION_STATE_WAITING,
          SUBMISSION_STATE_DISCUSSION,
        ],
      });
      if (!this._isMounted || !inProgress) return;
      this.setState({
        onGoing: group(inProgress, SUBMISSION_STATE_SUBMITTING).sort(byDepotDate),
        waiting: group(inProgress, SUBMISSION_STATE_WAITING).sort(byDepotDate),
        discussion: group(inProgress, SUBMISSION_STATE_DISCUSSION).sort(byStatusChange),
        loading: false,
      });

      const decided = await submissionService.list({
        submission_state_id: [SUBMISSION_STATE_WON, SUBMISSION_STATE_NOTWON],
        date_status_change: [
          moment().subtract(DECIDED_MAX_AGE_DAYS, "days").format("YYYY-MM-DD"),
          moment().add(1, "day").format("YYYY-MM-DD"),
        ],
      });
      if (!this._isMounted || !decided) return;
      this.setState({
        won: group(decided, SUBMISSION_STATE_WON).sort(byStatusChangeDesc),
        notWon: group(decided, SUBMISSION_STATE_NOTWON).sort(byStatusChangeDesc),
      });
    } catch {
      if (this._isMounted) this.setState({ loading: false });
    }
  }

  async fetchInterventions() {
    try {
      const res = await noteService.list({
        type_id: NOTE_TYPE_SSE,
        page_number: 1,
        per_page: 3,
        order_by: "date,id",
        order_direction: "DESC,DESC",
      });
      const list = res.data || res || [];
      if (this._isMounted) this.setState({ interventions: list.slice(0, 3) });
    } catch {
      /* keep previous interventions on failure */
    }
  }

  setFollowingUp(id, pending) {
    if (!this._isMounted) return;
    this.setState((prev) => ({ followingUp: { ...prev.followingUp, [id]: pending } }));
  }

  openSubmission(id) {
    if (!this.canEditSubmissions()) return;
    this.setState({ editingSubmissionId: id });
  }

  closeSubmissionModal(updated) {
    this.setState({ editingSubmissionId: null });
    if (updated) this.fetchSubmissions();
  }

  canEditSubmissions() {
    return this.context.can("edit", "Submission");
  }

  async followUp(sub) {
    if (!this.canEditSubmissions() || this.state.followingUp[sub.id]) return;
    this.setFollowingUp(sub.id, true);

    try {
      const updated = await submissionService.followUp(sub.id);
      if (!this._isMounted) return;
      this.setState((prev) => ({
        waiting: prev.waiting.map((row) =>
          row.id === sub.id
            ? {
                ...row,
                follow_up_count: updated.follow_up_count,
                date_last_follow_up: updated.date_last_follow_up,
              }
            : row,
        ),
      }));
    } catch {
      // Leave the card as it was; the 5-min refresh resyncs it.
    } finally {
      this.setFollowingUp(sub.id, false);
    }
  }

  onDragStart(e, sub, fromState) {
    this.dragged = { id: sub.id, from: fromState };
    e.dataTransfer.effectAllowed = "move";

    e.dataTransfer.setData("text/plain", String(sub.id));
    this.setState({ dragging: { id: sub.id, from: fromState } });
  }

  onDragEnd() {
    this.dragged = null;
    this.setState({ dragging: null, dragOverState: null });
    this.catchUpRefresh();
  }

  onDragOver(e, toState) {
    const dragged = this.dragged;
    if (!dragged || !canMove(dragged.from, toState)) return;


    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (this.state.dragOverState !== toState) this.setState({ dragOverState: toState });
  }

  onDragLeave(e, toState) {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (this.state.dragOverState === toState) this.setState({ dragOverState: null });
  }

  onDrop(e, toState) {
    e.preventDefault();
    const dragged = this.dragged;
    this.dragged = null;
    this.setState({ dragging: null, dragOverState: null });
    if (!dragged || !canMove(dragged.from, toState)) return;
    this.moveSubmission(dragged.id, dragged.from, toState);
  }

  async moveSubmission(id, fromState, toState, attrs = {}) {
    const fromKey = BUCKET_BY_STATE[fromState];
    const toKey = BUCKET_BY_STATE[toState];
    if (!fromKey || !toKey) return;

    const sub = (this.state[fromKey] || []).find((row) => row.id === id);
    if (!sub) return;

    if (AMOUNT_REQUIRED_STATES.includes(toState) && !sub.ammount && !attrs.ammount) {
      this.setState({ amountPrompt: { sub, fromState, toState, value: null } });
      return;
    }

    // The server stamps date_status_change on every state change; mirror it so the
    // badge and the sort order do not jump once the response lands.
    const moved = { ...sub, ...attrs, date_status_change: new Date().toISOString() };
    this.setState((prev) => ({
      [fromKey]: prev[fromKey].filter((row) => row.id !== id),
      [toKey]: [...prev[toKey], moved].sort(SORT_BY_BUCKET[toKey]),
    }));

    this.pendingMoves += 1;
    try {
      const updated = await submissionService.update({
        id,
        submission_state_id: toState,
        ...attrs,
      });
      if (!this._isMounted) return;
      this.setState((prev) => ({
        [toKey]: prev[toKey]
          .map((row) => (row.id === id ? { ...row, ...updated } : row))
          .sort(SORT_BY_BUCKET[toKey]),
      }));
    } catch {
      if (!this._isMounted) return;
      // Rollback
      this.setState((prev) => ({
        [toKey]: prev[toKey].filter((row) => row.id !== id),
        [fromKey]: prev[fromKey].some((row) => row.id === id)
          ? prev[fromKey]
          : [...prev[fromKey], sub].sort(SORT_BY_BUCKET[fromKey]),
      }));
      notification.error({
        message: "Le changement de statut n'a pas pu être enregistré",
        placement: "bottomRight",
      });
    } finally {
      this.pendingMoves -= 1;
      if (this._isMounted) this.catchUpRefresh();
    }
  }

  chartFilters() {
    return {
      date: [
        moment().startOf("year").format("YYYY-MM-DD"),
        moment().add(1, "day").format("YYYY-MM-DD"),
      ],
    };
  }

  renderIntervention(sse) {
    const who = sse.user ? sse.user.name : sse.visitor_name;
    const type = interventionType(sse.intervention_type);

    return (
      <article
        key={sse.id}
        className={`tvsub-intervention${type ? ` tvsub-intervention--${type.key}` : ""}`}
      >
        {type && <span className="tvsub-intervention-type">{type.label}</span>}
        <p className="tvsub-intervention-text">{`« ${sse.text || ""} »`}</p>
        <div className="tvsub-intervention-meta">
          <div className="tvsub-intervention-info">
            {(sse.project || {}).label && (
              <span className="tvsub-intervention-project">{sse.project.label}</span>
            )}
            <span className="tvsub-intervention-date">
              {moment(sse.date).format("dddd D MMMM YYYY")}
            </span>
          </div>
          <span className="tvsub-avatar" title={who || ""}>
            {initials(who)}
          </span>
        </div>
      </article>
    );
  }

  canDragFrom(stateId) {
    return (
      this.canEditSubmissions() &&
      Object.keys(BUCKET_BY_STATE).some((toState) => canMove(stateId, Number(toState)))
    );
  }

  confirmAmountPrompt() {
    const { amountPrompt } = this.state;
    if (!amountPrompt || !(amountPrompt.value > 0)) return;

    this.setState({ amountPrompt: null });
    this.moveSubmission(amountPrompt.sub.id, amountPrompt.fromState, amountPrompt.toState, {
      ammount: amountPrompt.value,
    });
  }

  renderAmountPrompt() {
    const { amountPrompt } = this.state;
    const targetLabel = amountPrompt ? STATE_LABELS[amountPrompt.toState] : "";

    return (
      <Modal
        visible={!!amountPrompt}
        title="Montant déposé"
        okText={`Passer à « ${targetLabel} »`}
        cancelText="Annuler"
        okButtonProps={{ disabled: !(amountPrompt && amountPrompt.value > 0) }}
        onOk={() => this.confirmAmountPrompt()}
        onCancel={() => this.setState({ amountPrompt: null })}
        destroyOnClose
      >
        {amountPrompt && (
          <>
            <p>
              <b>{amountPrompt.sub.title || amountPrompt.sub.num}</b> n'a pas de montant déposé.
              Indiquez-le pour passer la soumission « {targetLabel} ».
            </p>
            <InputNumber
              autoFocus
              min={0}
              precision={0}
              style={{ width: "100%" }}
              placeholder="Montant déposé"
              value={amountPrompt.value === null ? undefined : amountPrompt.value}
              onChange={(value) =>
                this.setState((prev) => ({ amountPrompt: { ...prev.amountPrompt, value } }))
              }
              onPressEnter={() => this.confirmAmountPrompt()}
            />
          </>
        )}
      </Modal>
    );
  }

  cardClasses(sub, cardKey, stateId) {
    const dragging = (this.state.dragging || {}).id === sub.id;
    return [
      "tvsub-card",
      `tvsub-card--${cardKey}`,
      this.canDragFrom(stateId) ? "tvsub-card--draggable" : "",
      dragging ? "tvsub-card--dragging" : "",
    ]
      .filter(Boolean)
      .join(" ");
  }

  dragProps(sub, stateId) {
    if (!this.canDragFrom(stateId)) return {};
    return {
      draggable: true,
      onDragStart: (e) => this.onDragStart(e, sub, stateId),
      onDragEnd: this.onDragEnd,
    };
  }

  dropProps(stateId) {
    if (!this.canEditSubmissions()) return {};
    return {
      onDragOver: (e) => this.onDragOver(e, stateId),
      onDragLeave: (e) => this.onDragLeave(e, stateId),
      onDrop: (e) => this.onDrop(e, stateId),
    };
  }

  renderFollowUp(sub) {
    const count = sub.follow_up_count || 0;
    const p = followUpBadge(sub);
    const pending = !!this.state.followingUp[sub.id];
    const canFollowUp = this.canEditSubmissions();

    if (!canFollowUp && !p) return null;

    return (
      <div className="tvsub-followup">
        {canFollowUp && (
          <button
            type="button"
            className="tvsub-followup-btn"
            disabled={pending}
            onClick={() => this.followUp(sub)}
          >
            {pending ? "…" : count > 0 ? `Relancer (${count})` : "Relancer"}
          </button>
        )}
        {p && (
          <span className={`tvsub-badge tvsub-followup-badge tvsub-badge--${p.key}`}>
            {p.label}
          </span>
        )}
      </div>
    );
  }

  renderCardTitle(sub) {
    const label = sub.title || sub.num;
    if (!this.canEditSubmissions()) return <span className="tvsub-card-title">{label}</span>;

    return (
      <span
        className="tvsub-card-title tvsub-card-title--link"
        onClick={() => this.openSubmission(sub.id)}
      >
        {label}
      </span>
    );
  }

  renderCard(sub, badge, stateId, withFollowUp = false) {
    const p = badge(sub);
    const cardKey = withFollowUp ? followUpCardKey(sub) : p.key;
    // A drag started on the icon would leave its tooltip floating until the drop.
    const tooltipProps = this.state.dragging ? { visible: false } : {};

    return (
      <li key={sub.id} className={this.cardClasses(sub, cardKey, stateId)} {...this.dragProps(sub, stateId)}>
        <div className="tvsub-card-main">
          {this.renderCardTitle(sub)}
          <span className="tvsub-card-num">
            {sub.num}
            {sub.client ? ` · ${sub.client.name}` : ""}
          </span>
          {withFollowUp && this.renderFollowUp(sub)}
        </div>
        <div className="tvsub-card-right">
          <span className="tvsub-avatar" title={sub.user ? sub.user.name : ""}>
            {initials(sub.user ? sub.user.name : "")}
          </span>
          <div className="tvsub-card-status">
            {AMOUNT_REQUIRED_STATES.includes(stateId) && !sub.ammount && (
              <Tooltip title="Montant déposé manquant" {...tooltipProps}>
                <Icon type="warning" theme="filled" className="tvsub-warning" />
              </Tooltip>
            )}
            <span className={`tvsub-badge tvsub-badge--${p.key}`}>{p.label}</span>
          </div>
        </div>
      </li>
    );
  }

  // Obtenu / Non obtenu cards: no deadline left to be urgent about.
  renderDecidedCard(sub, stateId) {
    return (
      <li key={sub.id} className={this.cardClasses(sub, "none", stateId)} {...this.dragProps(sub, stateId)}>
        <div className="tvsub-card-main">
          {this.renderCardTitle(sub)}
          <span className="tvsub-card-num">
            {sub.num}
            {sub.client ? ` · ${sub.client.name}` : ""}
          </span>
        </div>
        <div className="tvsub-card-right">
          <span className="tvsub-avatar" title={sub.user ? sub.user.name : ""}>
            {initials(sub.user ? sub.user.name : "")}
          </span>
        </div>
      </li>
    );
  }

  renderPanel(label, rows, stateId, renderRow = (sub) => this.renderDecidedCard(sub, stateId)) {
    const over = this.state.dragOverState === stateId;

    return (
      <section
        className={`tvsub-panel${over ? " tvsub-drop--over" : ""}`}
        {...this.dropProps(stateId)}
      >
        <h3 className="tvsub-panel-title">
          {label}
          <span className="tvsub-count">{rows.length}</span>
        </h3>
        <div className="tvsub-panel-body">
          {rows.length ? (
            <ul className="tvsub-list">{rows.map(renderRow)}</ul>
          ) : (
            <p className="tvsub-empty">Aucune soumission</p>
          )}
        </div>
      </section>
    );
  }

  renderColumn(label, rows, badge, stateId, withFollowUp = false) {
    const over = this.state.dragOverState === stateId;

    return (
      <section
        className={`tvsub-column${over ? " tvsub-drop--over" : ""}`}
        {...this.dropProps(stateId)}
      >
        <h2 className="tvsub-column-title">
          {label}
          <span className="tvsub-count">{rows.length}</span>
        </h2>
        {rows.length ? (
          <ul className="tvsub-list">
            {rows.map((sub) => this.renderCard(sub, badge, stateId, withFollowUp))}
          </ul>
        ) : (
          <p className="tvsub-empty">Aucune soumission</p>
        )}
      </section>
    );
  }

  render() {
    const {
      onGoing,
      waiting,
      discussion,
      notWon,
      won,
      interventions,
      refreshes,
      loading,
      dragging,
      editingSubmissionId,
    } = this.state;

    return (
      <div className="homeTvSub-page-wrap">
        <Helmet>{title("Soumissions TV")}</Helmet>
        <div className="homeTvSub-page">
          {/* ── Banner ── */}
          <header className="tvsub-banner">
            {/* Top: photo carousel */}
            <div className="tvsub-carousel">
              <CarouselPhotos />
            </div>

            {/* Mid row: logo · SSE-vs-tailgates pyramid · clock */}
            <div className="tvsub-midrow">
              <Logo className="tvsub-logo" />
              <div className="tvsub-pyramid">
                {SSE_VS_TAILGATES && (
                  <Chart
                    infos={SSE_VS_TAILGATES}
                    filters={this.chartFilters()}
                    refreshes={refreshes}
                  />
                )}
              </div>
              <Horloge onDashboard={true} />
            </div>

            {/* Bottom: 3 latest SSE interventions */}
            <div className="tvsub-interventions">
              {interventions.map((sse) => this.renderIntervention(sse))}
            </div>
          </header>

          {/* ── Three-column submissions board ── */}
          {loading ? (
            <p className="tvsub-empty">Chargement…</p>
          ) : (
            <div className={`tvsub-board${dragging ? " tvsub-board--dragging" : ""}`}>
              {this.renderColumn(
                "En soumission",
                onGoing,
                submittingBadge,
                SUBMISSION_STATE_SUBMITTING,
              )}
              {this.renderColumn(
                "En attente",
                waiting,
                waitingBadge,
                SUBMISSION_STATE_WAITING,
                true,
              )}
              <div className="tvsub-column tvsub-decided-stack">
                {this.renderPanel("En discussion", discussion, SUBMISSION_STATE_DISCUSSION, (sub) =>
                  this.renderCard(sub, discussionBadge, SUBMISSION_STATE_DISCUSSION),
                )}
                {this.renderPanel("Obtenu", won, SUBMISSION_STATE_WON)}
                {this.renderPanel("Non obtenu", notWon, SUBMISSION_STATE_NOTWON)}
              </div>
            </div>
          )}
        </div>

        {editingSubmissionId && (
          <SubmissionPage
            key={editingSubmissionId}
            edit
            modalOnly
            match={{ params: { id: String(editingSubmissionId) } }}
            location={{ search: "" }}
            onModalClose={(updated) => this.closeSubmissionModal(updated)}
          />
        )}

        {this.renderAmountPrompt()}
      </div>
    );
  }
}

HomeTvSubmissionsPage.contextType = AbilityContext;

function mapStateToProps(state) {
  return {
    userId: state.user.logged ? state.user.infos.id : null,
  };
}

export default connect(mapStateToProps)(HomeTvSubmissionsPage);
